// «Сбросить очки» внизу MOD-STATS (E05-T22, ux/05 §2): цена «Бесплатно» или «{n} гаек» — из `respec` в GET /me,
// клиент её не считает. Подтверждение — MOD-CONFIRM, затем POST /me/stats/reset {expectedPriceNuts}.
// Сменилась цена — показываем новую и ждём повторного тапа; не хватает гаек — тост с источником гаек.
import type { Player } from '@garages/contract';
import { useApp } from '../app/app.js';
import { num } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { online, player, saveCache, setWallet } from '../state/game.js';
import { showToast } from '../ui/toast.js';

export type RespecState = { kind: 'free' } | { kind: 'paid'; nuts: number } | { kind: 'blocked'; reason: string };

/** Состояние ссылки сброса по `respec` сервера. */
export function respecState(r: Player['respec']): RespecState {
  if (r.blockedReason) return { kind: 'blocked', reason: r.blockedReason };
  return r.nextIsFree || r.priceNuts === 0 ? { kind: 'free' } : { kind: 'paid', nuts: r.priceNuts };
}

const BLOCK_KEY: Record<string, string> = { world_top10: 'CL-RESPEC-TOP10', nothing_to_reset: 'CL-RESPEC-NOTHING', battle: 'CL-RESPEC-BATTLE' };

export function RespecLink() {
  const app = useApp();
  const me = player.value;
  if (!me) return null;
  const st = respecState(me.respec);
  const reset = async (expected: number) => {
    try {
      const r = await app.call('resetStats', { body: { expectedPriceNuts: expected }, idempotencyKey: app.idem() });
      player.value = r.player;
      setWallet(r.wallet);
      saveCache();
      app.haptic('success');
      showToast(tr('CL-RESPEC-DONE', { n: r.player.statPointsFree }), 'success');
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      app.haptic('error');
      if (code === 'PRICE_CHANGED') {
        const p = await app.call('getMe').catch(() => null);
        if (p) player.value = p;
        showToast(tr('CL-RESPEC-PRICE', { n: num(p?.respec.priceNuts ?? 0) }), 'error');
        return;
      }
      if (code === 'NOT_ENOUGH_NUTS') {
        showToast(tr('CL-RESPEC-NUTS'), 'error', { text: tr('CL-RESPEC-WHERE'), onClick: () => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'nuts' }) });
        return;
      }
      if (code === 'IN_BATTLE') { showToast(tr('CL-RESPEC-BATTLE'), 'error'); return; }
      if (code === 'REQUIREMENT_NOT_MET') {
        const reason = String((e as ApiCallError).details?.['reason'] ?? '');
        showToast(tr(BLOCK_KEY[reason] ?? 'CL-RESPEC-NOTHING'), 'error');
        void app.call('getMe').then((p) => { player.value = p; }).catch(() => {});
        return;
      }
      app.handleError(e, { resource: tr('CL-RESPEC-SAFE') });
    }
  };
  const price = st.kind === 'paid' ? st.nuts : 0;
  const open = () => app.nav.openSheet('MOD-CONFIRM', {
    title: tr('CL-RESPEC-TITLE'),
    text: st.kind === 'free' ? tr('CL-RESPEC-TEXT-FREE') : tr('CL-RESPEC-TEXT', { n: num(price) }),
    risky: st.kind === 'free' ? tr('CL-RESPEC-GO-FREE') : tr('CL-RESPEC-GO', { n: num(price) }),
    safe: tr('CL-RESPEC-KEEP'),
    onRisky: () => { void reset(price); },
  });
  return (
    <div class="respec col" data-testid="respec">
      <button class="respec-link" data-testid="respec-link" disabled={st.kind === 'blocked' || !online.value} onClick={open}>
        {tr('UI-STATS-RESPEC')} · {st.kind === 'free' ? tr('CL-RESPEC-FREE') : st.kind === 'paid' ? tr('CL-RESPEC-PRICE-NUTS', { n: num(st.nuts) }) : ''}
      </button>
      {st.kind === 'blocked' ? <span class="small muted" data-testid="respec-why">{tr(BLOCK_KEY[st.reason] ?? 'CL-RESPEC-NOTHING')}</span> : null}
      {me.respec.resetsInSeason ? <span class="small muted">{tr('CL-RESPEC-COUNT', { n: me.respec.resetsInSeason })}</span> : null}
    </div>
  );
}
